import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { QueryOptions } from 'odata-query';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_BASE_URL } from '../../../web-api-client';
import { Collection, Country, GeneralRatesRuleEngine } from '../models';

interface GeneralRateRulesEditorApiItem {
  generalRateRulesEditorId: number;
  entityTypeId: number;
  incomeTypeId?: number;
  incomeTypeName?: string;
  incomeType?: { name?: string };
  taxResultItemTypeId?: number;
  taxResultItemType?: { name?: string };
  entityType?: { name?: string; country?: Country };
  investmentJurisdictionId?: number;
  investmentJurisdiction?: { country?: { name?: string } };
  securityTypeId?: number;
  securityType?: { name?: string };
  description?: string;
  generalRateRulesEditorRates?: {
    generalRateRulesEditorRateId?: number;
    rate?: number;
  }[];
  created?: string;
  modified?: string;
}

interface LocalFilter {
  key: string;
  condition: Record<string, unknown>;
  originalCondition?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class GeneralRatesRuleEngineService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL, { optional: true }) ?? '';

  // Controller route: api/v{version}/[controller] -> GeneralRateRulesEditors
  private readonly endpoint = '/api/v1/GeneralRateRulesEditors';

  get(
    query?: Partial<QueryOptions<GeneralRatesRuleEngine>>
  ): Observable<Collection<GeneralRatesRuleEngine>> {
    let params = new HttpParams();

    // Extract and store local filters for client-side filtering
    const localFilterKeys = [
      'entityTypeName',
      'investmentJurisdictionNames',
      'securityTypeNames',
      'incomeTypeName',
      'rateDisplay',
      'explanation',
      'investmentJurisdictionId',
      'description',
    ];
    const localFilters = this.extractLocalFilters(query, localFilterKeys);

    const localOrderByKeys = ['incomeTypeName', 'rate'];
    const localOrderBy = this.extractLocalOrderBy(query, localOrderByKeys);

    // Build OData filter, excluding local-only filters
    const queryWithoutLocalFilters = this.removeLocalFiltersFromQuery(
      query,
      localFilterKeys
    );
    const filteredQuery = this.removeLocalOrderByFromQuery(
      queryWithoutLocalFilters,
      localOrderByKeys
    );
    const filter = buildQueryFilter(filteredQuery);
    const shouldPaginateLocally = localFilters.length > 0 || !!localOrderBy;

    if (filter) params = params.set('$filter', filter);
    if (!shouldPaginateLocally && query?.top !== undefined)
      params = params.set('$top', query.top.toString());
    if (!shouldPaginateLocally && query?.skip !== undefined)
      params = params.set('$skip', query.skip.toString());
    if (filteredQuery?.orderBy)
      params = params.set('$orderby', filteredQuery.orderBy.toString());
    if (query?.count !== undefined)
      params = params.set('$count', query.count.toString());

    return this.http
      .get<{
        '@odata.count'?: number;
        value: GeneralRateRulesEditorApiItem[];
      }>(`${this.baseUrl}${this.endpoint}`, { params })
      .pipe(
        map(response => {
          let items = (response.value ?? []).map(item =>
            this.mapToUiModel(item)
          );

          // Apply local filters to the mapped items
          if (localFilters.length > 0) {
            items = this.applyLocalFilters(items, localFilters);
          }

          if (localOrderBy) {
            items = this.applyLocalOrderBy(items, localOrderBy);
          }

          const total = shouldPaginateLocally
            ? items.length
            : (response['@odata.count'] ?? 0);

          if (shouldPaginateLocally) {
            items = this.paginateLocalItems(items, query);
          }

          return {
            total,
            items,
          };
        }),
        catchError(error =>
          this.handleError(error, 'Error al obtener registros')
        )
      );
  }

  getById(id: number): Observable<GeneralRatesRuleEngine> {
    return this.http
      .get<GeneralRateRulesEditorApiItem>(
        `${this.baseUrl}${this.endpoint}/${id}`
      )
      .pipe(
        map(item => this.mapToUiModel(item)),
        catchError(error =>
          this.handleError(error, `Error al obtener registro con ID: ${id}`)
        )
      );
  }

  create(item: GeneralRatesRuleEngine): Observable<GeneralRatesRuleEngine> {
    const ratesPayload = this.buildRatesPayload(item);

    return this.http
      .post<GeneralRateRulesEditorApiItem>(`${this.baseUrl}${this.endpoint}`, {
        entityTypeId: item.entityTypeId,
        investmentJurisdictionId: item.investmentJurisdictionIds?.[0],
        securityTypeId: item.securityTypeIds?.[0],
        taxResultItemTypeId: item.incomeTypeId,
        description: item.explanation,
        generalRateRulesEditorRates: ratesPayload.map(rate => ({ rate })),
      })
      .pipe(
        map(response => {
          const mapped = this.mapToUiModel(response);
          return {
            ...mapped,
            incomeTypeId: mapped.incomeTypeId ?? item.incomeTypeId,
            incomeTypeName: mapped.incomeTypeName || item.incomeTypeName,
          };
        }),
        catchError(error => this.handleError(error, 'Error al crear registro'))
      );
  }

  update(item: GeneralRatesRuleEngine): Observable<void> {
    const ratesPayload = this.buildRatesPayload(item);

    return this.http
      .put<void>(
        `${this.baseUrl}${this.endpoint}/${item.generalRatesRuleEngineId}`,
        {
          generalRateRulesEditorId: item.generalRatesRuleEngineId,
          description: item.explanation,
          generalRateRulesEditorRates:
            ratesPayload.length === 1 && item.generalRateRulesEditorRateId
              ? [
                  {
                    generalRateRulesEditorRateId:
                      item.generalRateRulesEditorRateId,
                    rate: ratesPayload[0],
                  },
                ]
              : ratesPayload.map(rate => ({ rate })),
        }
      )
      .pipe(
        catchError(error =>
          this.handleError(error, 'Error al actualizar registro')
        )
      );
  }

  delete(id: number): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}${this.endpoint}/${id}`)
      .pipe(
        catchError(error =>
          this.handleError(error, `Error al eliminar registro con ID: ${id}`)
        )
      );
  }

  // Maps the API response (GeneralRateRulesEditorResponse) to the UI model
  private mapToUiModel(
    item: GeneralRateRulesEditorApiItem
  ): GeneralRatesRuleEngine {
    const incomeTypeName =
      item.incomeTypeName ??
      item.incomeType?.name ??
      item.taxResultItemType?.name ??
      '';
    const incomeTypeId = item.incomeTypeId ?? item.taxResultItemTypeId;
    const rates = (item.generalRateRulesEditorRates ?? [])
      .map(rateItem => rateItem.rate)
      .filter((rate): rate is number => rate !== undefined && rate !== null);
    const firstRate = item.generalRateRulesEditorRates?.[0];
    const rateDisplay =
      rates.length > 0
        ? rates.map(r => String(r)).join(', ')
        : String(firstRate?.rate ?? 0);
    const explanationHtml = item.description ?? '';
    const explanation = this.extractPlainTextFromHtml(explanationHtml);

    return {
      generalRatesRuleEngineId: item.generalRateRulesEditorId,
      entityTypeId: item.entityTypeId,
      entityTypeName: item.entityType?.name ?? '',
      generalRateRulesEditorRateId: firstRate?.generalRateRulesEditorRateId,
      rate: firstRate?.rate ?? 0,
      rates,
      rateDisplay,
      country: item.entityType?.country,
      investmentJurisdictionIds: item.investmentJurisdictionId
        ? [item.investmentJurisdictionId]
        : [],
      investmentJurisdictionNames: item.investmentJurisdiction?.country?.name
        ? [item.investmentJurisdiction.country.name]
        : [],
      securityTypeIds: item.securityTypeId ? [item.securityTypeId] : [],
      securityTypeNames: item.securityType?.name
        ? [item.securityType.name]
        : [],
      incomeTypeId,
      incomeTypeName,
      explanation,
      explanationHtml,
      created: item.created ? new Date(item.created) : undefined,
      modified: item.modified ? new Date(item.modified) : undefined,
    };
  }

  private extractPlainTextFromHtml(html: string): string {
    if (!html || !/<[^>]+>/.test(html)) {
      return html ?? '';
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const root = doc.body;

    const toText = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent ?? '';
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
      }

      const element = node as HTMLElement;
      const tag = element.tagName.toLowerCase();

      if (tag === 'li') {
        const itemText = Array.from(element.childNodes)
          .map(toText)
          .join('')
          .trim();
        return itemText ? `- ${itemText}\n` : '';
      }

      const text = Array.from(element.childNodes).map(toText).join('');

      if (tag === 'p' || tag === 'div' || tag === 'br') {
        return `${text}\n`;
      }

      if (tag === 'ul' || tag === 'ol') {
        return `${text}\n`;
      }

      return text;
    };

    return Array.from(root.childNodes)
      .map(toText)
      .join('')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  /**
   * Build the payload for rates based on the provided item.
   * @param item The general rates rule engine item containing rates.
   * @returns An array of rates extracted from the item.
   */
  private buildRatesPayload(item: GeneralRatesRuleEngine): number[] {
    const rates = (item.rates ?? []).filter(
      (rate): rate is number => rate !== undefined && rate !== null
    );

    if (rates.length > 0) {
      return rates;
    }

    return [item.rate ?? 0];
  }

  // Handles HTTP errors and logs them
  private handleError(
    error: HttpErrorResponse,
    context: string
  ): Observable<never> {
    const errorMessage = this.extractErrorMessage(error);
    const errorStatus = error.status || 'N/A';

    console.error(
      `[GeneralRatesRuleEngineService] ${context} - Status: ${errorStatus}`,
      {
        message: errorMessage,
        status: error.status,
        url: error.url,
        statusText: error.statusText,
        error: error.error,
      }
    );

    return throwError(() => ({
      context,
      message: errorMessage,
      status: errorStatus,
      originalError: error,
    }));
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    const payload = error.error;

    if (typeof payload === 'string' && payload.trim().length > 0) {
      return payload;
    }

    if (payload && typeof payload === 'object') {
      const obj = payload as {
        message?: string;
        title?: string;
        detail?: string;
      };
      return (
        obj.message ||
        obj.title ||
        obj.detail ||
        error.message ||
        'Error desconocido'
      );
    }

    return error.message || 'Error desconocido';
  }

  /**
   * Extracts local filter conditions by checking if query.filter contains local-only keys
   */
  private extractLocalFilters(
    query: Partial<QueryOptions<GeneralRatesRuleEngine>> | undefined,
    localKeys: string[]
  ): LocalFilter[] {
    if (!query?.filter) return [];

    const filters: LocalFilter[] = [];
    const filterObj = query.filter as Record<string, unknown>;

    // Handle nested filter structure with 'or' or 'and' operators
    if (filterObj['or'] && Array.isArray(filterObj['or'])) {
      (filterObj['or'] as Record<string, unknown>[]).forEach(condition => {
        localKeys.forEach(key => {
          if (condition[key]) {
            filters.push({
              key,
              condition: this.normalizeCondition(condition[key]),
              originalCondition: condition,
            });
          }
        });
      });
    } else if (filterObj['and'] && Array.isArray(filterObj['and'])) {
      (filterObj['and'] as Record<string, unknown>[]).forEach(condition => {
        localKeys.forEach(key => {
          if (condition[key]) {
            filters.push({
              key,
              condition: this.normalizeCondition(condition[key]),
              originalCondition: condition,
            });
          }
        });
      });
    } else {
      // Handle flat structure (direct keys)
      localKeys.forEach(key => {
        if (filterObj[key]) {
          filters.push({
            key,
            condition: this.normalizeCondition(filterObj[key]),
          });
        }
      });
    }

    return filters;
  }

  /**
   * Creates a new query object without local-only filter keys
   */
  private removeLocalFiltersFromQuery(
    query: Partial<QueryOptions<GeneralRatesRuleEngine>> | undefined,
    localKeys: string[]
  ): Partial<QueryOptions<GeneralRatesRuleEngine>> | undefined {
    if (!query) return query;

    const newQuery = { ...query };

    if (newQuery.filter) {
      const filterObj = newQuery.filter as Record<string, unknown>;

      // Handle nested filter structure with 'or' operator
      if (filterObj['or'] && Array.isArray(filterObj['or'])) {
        const cleanedOr = (filterObj['or'] as Record<string, unknown>[]).filter(
          condition => {
            // Keep conditions that don't have any local keys
            if (condition['and'] && Array.isArray(condition['and'])) {
              const nestedAnd = condition['and'] as Record<string, unknown>[];
              return nestedAnd.some(
                nestedCondition =>
                  !localKeys.some(key => key in nestedCondition)
              );
            }

            if (condition['or'] && Array.isArray(condition['or'])) {
              const nestedOr = condition['or'] as Record<string, unknown>[];
              return nestedOr.some(
                nestedCondition =>
                  !localKeys.some(key => key in nestedCondition)
              );
            }

            return !localKeys.some(key => key in condition);
          }
        );

        if (cleanedOr.length > 0) {
          newQuery.filter = { or: cleanedOr };
        } else {
          newQuery.filter = undefined;
        }
      }
      // Handle nested filter structure with 'and' operator
      else if (filterObj['and'] && Array.isArray(filterObj['and'])) {
        const cleanedAnd = (
          filterObj['and'] as Record<string, unknown>[]
        ).filter(condition => {
          return !localKeys.some(key => key in condition);
        });

        if (cleanedAnd.length > 0) {
          newQuery.filter = { and: cleanedAnd };
        } else {
          newQuery.filter = undefined;
        }
      }
      // Handle flat structure (direct keys)
      else {
        const cleanFilter = { ...filterObj };
        localKeys.forEach(key => {
          delete cleanFilter[key];
        });

        newQuery.filter =
          Object.keys(cleanFilter).length > 0 ? cleanFilter : undefined;
      }
    }

    return newQuery;
  }

  private extractLocalOrderBy(
    query: Partial<QueryOptions<GeneralRatesRuleEngine>> | undefined,
    localKeys: string[]
  ): { key: keyof GeneralRatesRuleEngine; direction: 'asc' | 'desc' } | null {
    const orderBy = query?.orderBy;
    if (!orderBy) return null;

    const orderByExpression = String(orderBy).split(',')[0]?.trim();
    if (!orderByExpression) return null;

    const [field, directionToken] = orderByExpression.split(/\s+/);
    if (!localKeys.includes(field)) return null;

    const direction = directionToken?.toLowerCase() === 'desc' ? 'desc' : 'asc';

    return {
      key: field as keyof GeneralRatesRuleEngine,
      direction,
    };
  }

  private removeLocalOrderByFromQuery(
    query: Partial<QueryOptions<GeneralRatesRuleEngine>> | undefined,
    localKeys: string[]
  ): Partial<QueryOptions<GeneralRatesRuleEngine>> | undefined {
    if (!query?.orderBy) return query;

    const newQuery = { ...query };
    const orderByExpression = String(newQuery.orderBy).split(',')[0]?.trim();
    const [field] = (orderByExpression ?? '').split(/\s+/);

    if (localKeys.includes(field)) {
      newQuery.orderBy = undefined;
    }

    return newQuery;
  }

  private applyLocalOrderBy(
    items: GeneralRatesRuleEngine[],
    localOrderBy: {
      key: keyof GeneralRatesRuleEngine;
      direction: 'asc' | 'desc';
    }
  ): GeneralRatesRuleEngine[] {
    const directionMultiplier = localOrderBy.direction === 'asc' ? 1 : -1;

    return [...items].sort((a, b) => {
      const aRawValue = a[localOrderBy.key];
      const bRawValue = b[localOrderBy.key];
      const aValue = Array.isArray(aRawValue) ? aRawValue[0] : aRawValue;
      const bValue = Array.isArray(bRawValue) ? bRawValue[0] : bRawValue;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * directionMultiplier;
      }

      if (aValue instanceof Date && bValue instanceof Date) {
        return (aValue.getTime() - bValue.getTime()) * directionMultiplier;
      }

      const aComparable = (aValue ?? '').toString().toLowerCase();
      const bComparable = (bValue ?? '').toString().toLowerCase();

      if (aComparable < bComparable) return -1 * directionMultiplier;
      if (aComparable > bComparable) return 1 * directionMultiplier;
      return 0;
    });
  }

  /**
   * Applies local filters to items
   */
  private applyLocalFilters(
    items: GeneralRatesRuleEngine[],
    filters: LocalFilter[]
  ): GeneralRatesRuleEngine[] {
    if (filters.length === 0) return items;

    const filtersByKey = new Map<string, LocalFilter[]>();
    for (const filter of filters) {
      const stored = filtersByKey.get(filter.key) ?? [];
      stored.push(filter);
      filtersByKey.set(filter.key, stored);
    }

    return items.filter(item => {
      // AND between different columns, OR within the same column conditions.
      for (const [key, keyFilters] of filtersByKey.entries()) {
        const candidateValues = this.getLocalFilterCandidateValues(item, {
          key,
          condition: {},
        });

        const hasAnyMatchInKey = keyFilters.some(filter =>
          this.matchesLocalCondition(candidateValues, filter.condition)
        );

        if (!hasAnyMatchInKey) {
          return false;
        }
      }

      return true;
    });
  }

  private normalizeCondition(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }

    return { contains: value };
  }

  private getLocalFilterCandidateValues(
    item: GeneralRatesRuleEngine,
    filter: LocalFilter
  ): unknown[] {
    switch (filter.key) {
      case 'entityTypeId':
        return [item.entityTypeId];
      case 'investmentJurisdictionId':
        return item.investmentJurisdictionIds ?? [];
      case 'securityTypeId':
        return item.securityTypeIds ?? [];
      case 'rateDisplay': {
        const rateTokens = (item.rateDisplay ?? '')
          .split(',')
          .map(value => value.trim())
          .filter(value => value.length > 0);

        const fromRates = (item.rates ?? []).map(value => String(value));
        const fromRate =
          item.rate !== undefined && item.rate !== null
            ? [String(item.rate)]
            : [];

        return [...rateTokens, ...fromRates, ...fromRate];
      }
      case 'description':
        return [item.explanation ?? '', item.explanationHtml ?? ''];
      case 'explanation':
        return [item.explanation ?? '', item.explanationHtml ?? ''];
      default: {
        const itemKey = filter.key as keyof GeneralRatesRuleEngine;
        const value = item[itemKey];

        if (value === undefined || value === null) {
          return [];
        }

        return Array.isArray(value) ? value : [value];
      }
    }
  }

  private matchesLocalCondition(
    candidateValues: unknown[],
    condition: Record<string, unknown>
  ): boolean {
    if (candidateValues.length === 0) {
      return false;
    }

    if (Array.isArray(condition['in'])) {
      const expectedRaw = (condition['in'] as unknown[]).filter(
        value => value !== undefined && value !== null && value !== ''
      );

      if (expectedRaw.length === 0) {
        return false;
      }

      const expectedNumbers = expectedRaw
        .map(value => Number(value))
        .filter(value => !isNaN(value));

      const candidateNumbers = candidateValues
        .map(value => Number(value))
        .filter(value => !isNaN(value));

      if (expectedNumbers.length > 0 && candidateNumbers.length > 0) {
        return candidateNumbers.some(value => expectedNumbers.includes(value));
      }

      const expectedStrings = expectedRaw.map(value =>
        String(value).toLowerCase().trim()
      );

      return candidateValues.some(value =>
        expectedStrings.includes(
          String(value ?? '')
            .toLowerCase()
            .trim()
        )
      );
    }

    const searchValue = condition['contains'];

    if (
      searchValue === undefined ||
      searchValue === null ||
      searchValue === ''
    ) {
      return false;
    }

    const search = String(searchValue).toLowerCase();
    return candidateValues.some(value =>
      String(value ?? '')
        .toLowerCase()
        .includes(search)
    );
  }

  private paginateLocalItems(
    items: GeneralRatesRuleEngine[],
    query: Partial<QueryOptions<GeneralRatesRuleEngine>> | undefined
  ): GeneralRatesRuleEngine[] {
    const skip = query?.skip ?? 0;
    const top = query?.top;

    if (top === undefined) {
      return items.slice(skip);
    }

    return items.slice(skip, skip + top);
  }
}
